import React, { useState, useEffect, useCallback } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import {
  Settings, Users, MapPin, Plus, Shield, User, Lock, Eye, EyeOff,
  ChevronDown, ChevronRight, Building2, Activity, Monitor, Clock,
  CheckSquare, Square, Search, Filter, Timer, Mail, Send, Play,
  Smartphone, Trash2, AlertTriangle, CheckCircle, ShieldCheck, QrCode, Copy,
  FileSpreadsheet, KeyRound, Phone, ArrowRightLeft, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { isAdmin, getUser } from '../lib/auth';
import GrootboekTab from '../components/tw1/GrootboekTab';

function EmailPreferencesSection() {
  const [prefs, setPrefs] = React.useState({ task_reminders: false, daily_summary: false, weekly_report: false });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get('/api/users/email-preferences')
      .then(r => setPrefs(r.data.email_preferences || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async (newPrefs) => {
    setPrefs(newPrefs);
    setSaving(true);
    try {
      await api.put('/api/users/email-preferences', { email_preferences: newPrefs });
      toast.success('E-mail voorkeuren opgeslagen');
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="tw1-card p-5"><p className="text-sm text-[#526071]">Laden...</p></div>;

  return (
    <div className="tw1-card p-5" data-testid="email-prefs-card">
      <h3 className="font-semibold text-sm text-[#0b1220] mb-1 flex items-center gap-2">
        E-mail Notificatie Voorkeuren
      </h3>
      <p className="text-xs text-[#526071] mb-4">Kies welke e-mails je wilt ontvangen. Standaard staat alles uit.</p>
      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" data-testid="pref-task-reminders">
          <input
            type="checkbox"
            checked={prefs.task_reminders || false}
            onChange={e => save({ ...prefs, task_reminders: e.target.checked })}
            className="rounded border-gray-300 text-[#003366] focus:ring-[#003366]"
            disabled={saving}
          />
          <div>
            <p className="text-sm font-medium text-[#0b1220]">Taak-herinneringen</p>
            <p className="text-xs text-[#526071]">Ontvang elke ochtend een e-mail met je openstaande en overdue taken</p>
          </div>
        </label>
        <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" data-testid="pref-daily-summary">
          <input
            type="checkbox"
            checked={prefs.daily_summary || false}
            onChange={e => save({ ...prefs, daily_summary: e.target.checked })}
            className="rounded border-gray-300 text-[#003366] focus:ring-[#003366]"
            disabled={saving}
          />
          <div>
            <p className="text-sm font-medium text-[#0b1220]">Dagelijkse samenvatting</p>
            <p className="text-xs text-[#526071]">Ontvang elke ochtend een overzicht van taken, exports en activiteit</p>
          </div>
        </label>
        <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" data-testid="pref-weekly-report">
          <input
            type="checkbox"
            checked={prefs.weekly_report || false}
            onChange={e => save({ ...prefs, weekly_report: e.target.checked })}
            className="rounded border-gray-300 text-[#003366] focus:ring-[#003366]"
            disabled={saving}
          />
          <div>
            <p className="text-sm font-medium text-[#0b1220]">Weekrapport (elke maandag)</p>
            <p className="text-xs text-[#526071]">Ontvang elke maandagochtend een samenvatting van de afgelopen week</p>
          </div>
        </label>
      </div>
    </div>
  );
}


const ALL_PAGES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'tw1-memo', label: 'TW1 Memo' },
  { key: 'truckshop-memo', label: 'Truckshop Memo' },
  { key: 'maandafsluiting', label: 'Maandafsluiting' },
  { key: 'ptw-import', label: 'PTW Import' },
  { key: 'incassos', label: "Incasso's" },
  { key: 'betalingen', label: 'Betalingen' },
  { key: 'salaris', label: 'Salaris' },
  { key: 'sepa', label: 'SEPA' },
  { key: 'storno', label: 'Storno' },
  { key: 'logboek', label: 'Logboek' },
  { key: 'instellingen', label: 'Instellingen' },
];

/* ─── PHONE DUTY TAB ─── */
function PhoneDutyTab() {
  const [eligible, setEligible] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [weekData, setWeekData] = useState([]);
  const [weekLabel, setWeekLabel] = useState('');
  const [weekMonday, setWeekMonday] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState({});

  const loadWeek = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(monday.getDate() - monday.getDay() + 1 + offset * 7);
      const iso = monday.toISOString().split('T')[0];
      const yr = monday.getFullYear();
      const startOfYear = new Date(yr, 0, 1);
      const days = Math.floor((monday - startOfYear) / 86400000);
      const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
      const wk = `${yr}-W${String(weekNum).padStart(2, '0')}`;

      const [weekRes, eligRes, usersRes] = await Promise.all([
        api.get(`/api/phone-duty?week=${wk}`),
        api.get('/api/phone-duty/eligible'),
        api.get('/api/settings/users'),
      ]);
      setWeekData(weekRes.data.days || []);
      setWeekLabel(weekRes.data.week || wk);
      setWeekMonday(weekRes.data.monday || iso);
      setEligible(eligRes.data.users || []);
      setAllUsers(usersRes.data.users || []);
      // Pre-fill assignments
      const a = {};
      (weekRes.data.days || []).forEach(d => {
        if (d.user_email) a[d.date] = d.user_email;
      });
      setAssignments(a);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWeek(weekOffset); }, [weekOffset, loadWeek]);

  const handleSaveWeek = async () => {
    setSaving(true);
    try {
      const bulk = Object.entries(assignments)
        .filter(([, email]) => email)
        .map(([date, user_email]) => ({ date, user_email }));
      await api.post('/api/phone-duty/bulk', { assignments: bulk });
      toast.success('Weekplanning opgeslagen');
      await loadWeek(weekOffset);
    } catch (err) {
      toast.error('Opslaan mislukt');
    } finally { setSaving(false); }
  };

  const handleToggleEligible = async (email, current) => {
    try {
      await api.put(`/api/settings/users/${encodeURIComponent(email)}/phone-duty-eligible`, { eligible: !current });
      toast.success('Bijgewerkt');
      await loadWeek(weekOffset);
    } catch (err) {
      toast.error('Bijwerken mislukt');
    }
  };

  const NL_DAY_NAMES = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag'];
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Weekplanner */}
      <div className="tw1-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-sm text-[#0b1220] flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#003366]" /> Weekplanner — {weekLabel}
            </h3>
            <p className="text-xs text-[#526071] mt-0.5">Selecteer per dag wie telefoondienst heeft (10:00 — 14:00)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w - 1)} data-testid="pd-prev-week">
              <ChevronDown className="w-4 h-4 rotate-90" />
            </Button>
            {weekOffset !== 0 && (
              <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)} className="text-xs">Vandaag</Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w + 1)} data-testid="pd-next-week">
              <ChevronDown className="w-4 h-4 -rotate-90" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
        ) : (
          <div className="space-y-2">
            {weekData.map((day, i) => {
              const isToday = day.date === todayStr;
              return (
                <div key={day.date}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isToday ? 'border-[#003366]/20 bg-[#003366]/5' : 'border-gray-100'}`}
                  data-testid={`pd-day-${i}`}
                >
                  <span className="w-24 text-sm font-medium text-[#0b1220]">
                    {NL_DAY_NAMES[i]}
                    {isToday && <Badge className="ml-2 text-[9px] bg-[#003366] text-white">vandaag</Badge>}
                  </span>
                  <span className="text-xs font-mono text-[#526071] w-20">{day.date}</span>
                  <Select
                    value={assignments[day.date] || 'none'}
                    onValueChange={(val) => setAssignments(prev => ({ ...prev, [day.date]: val === 'none' ? '' : val }))}
                  >
                    <SelectTrigger className="h-9 flex-1" data-testid={`pd-select-${i}`}>
                      <SelectValue placeholder="— Niet ingepland —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Niet ingepland —</SelectItem>
                      {eligible.map(u => (
                        <SelectItem key={u.email} value={u.email}>{u.naam}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {day.swapped_from && (
                    <span className="text-[10px] text-[#526071] flex items-center gap-1">
                      <ArrowRightLeft className="w-3 h-3" /> van {day.swapped_from}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSaveWeek}
            disabled={saving}
            className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-sm"
            data-testid="pd-save-week"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Opslaan...' : 'Week opslaan'}
          </Button>
        </div>
      </div>

      {/* Deelnemers beheren */}
      <div className="tw1-card p-5">
        <h3 className="font-semibold text-sm text-[#0b1220] mb-1 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#003366]" /> Deelnemers telefoondienst
        </h3>
        <p className="text-xs text-[#526071] mb-4">Vink aan wie meedoet aan de telefoondienst planning</p>
        <div className="space-y-2">
          {allUsers.map(u => {
            const isEligible = u.phone_duty_eligible === true;
            return (
              <div key={u.email} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid={`pd-user-${u.email}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#003366]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#003366]">{u.naam?.[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#0b1220]">{u.naam}</p>
                    <p className="text-xs text-[#526071]">{u.email}</p>
                  </div>
                </div>
                <Button
                  variant={isEligible ? "default" : "outline"}
                  size="sm"
                  className={`text-xs ${isEligible ? 'bg-[#1e8c45] hover:bg-[#167a38] text-white' : ''}`}
                  onClick={() => handleToggleEligible(u.email, isEligible)}
                  data-testid={`pd-toggle-${u.email}`}
                >
                  {isEligible ? 'Actief' : 'Inactief'}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function InstellingenPage() {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationDetails, setLocationDetails] = useState([]);
  const [expandedLoc, setExpandedLoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', naam: '', rol: 'gebruiker', wachtwoord: 'Welkom2026!' });
  const [addError, setAddError] = useState('');
  const admin = isAdmin();
  const currentUser = getUser();

  // Password change state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Activity log state
  const [activities, setActivities] = useState([]);
  const [actFilter, setActFilter] = useState({ gebruiker: '', actie: '' });
  const [actLoading, setActLoading] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessLoading, setSessLoading] = useState(false);

  // Screensaver settings
  const [screensaverTimeout, setScreensaverTimeout] = useState(5);
  const [seasonalThemes, setSeasonalThemes] = useState(true);

  // Email settings state
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState('');
  const [deviceUser, setDeviceUser] = useState('');
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // 2FA state
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [disablePw, setDisablePw] = useState('');

  // Changelog state
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [changelogHasNew, setChangelogHasNew] = useState(false);
  const [showAddChangelog, setShowAddChangelog] = useState(false);
  const [clForm, setClForm] = useState({ versie: '', titel: '', type: 'nieuw', beschrijving: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const [locRes, locDetailRes] = await Promise.all([
          api.get('/api/settings/locations'),
          api.get('/api/settings/locations/details'),
        ]);
        setLocations(locRes.data.locations);
        setLocationDetails(locDetailRes.data.locations || []);
        if (admin) {
          const [usersRes, appRes] = await Promise.all([
            api.get('/api/settings/users'),
            api.get('/api/settings/app'),
          ]);
          setUsers(usersRes.data.users);
          setScreensaverTimeout(appRes.data.screensaver_timeout || 5);
          setSeasonalThemes(appRes.data.seasonal_themes !== false);
        }
        // Check 2FA status
        const meRes = await api.get('/api/auth/me');
        setTwoFaEnabled(meRes.data.totp_enabled || false);
      } catch (err) {
        console.error('Instellingen laden mislukt:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [admin]);

  const handleAddUser = async () => {
    setAddError('');
    try {
      await api.post('/api/settings/users', newUser);
      setShowAddUser(false);
      setNewUser({ email: '', naam: '', rol: 'gebruiker', wachtwoord: 'Welkom2026!' });
      const res = await api.get('/api/settings/users');
      setUsers(res.data.users);
      toast.success('Gebruiker toegevoegd');
    } catch (err) {
      setAddError(err.response?.data?.detail || 'Gebruiker toevoegen mislukt');
    }
  };

  const handleUpdateUser = async (email, field, value) => {
    try {
      await api.put(`/api/settings/users/${encodeURIComponent(email)}`, { [field]: value });
      const res = await api.get('/api/settings/users');
      setUsers(res.data.users);
      toast.success('Gebruiker bijgewerkt');
    } catch (err) {
      toast.error('Bijwerken mislukt');
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(null);

  const handleDeleteUser = async (email) => {
    try {
      const res = await api.delete(`/api/settings/users/${encodeURIComponent(email)}`);
      toast.success(res.data.message || 'Gebruiker verwijderd');
      setDeleteConfirm(null);
      const usersRes = await api.get('/api/settings/users');
      setUsers(usersRes.data.users);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verwijderen mislukt');
      setDeleteConfirm(null);
    }
  };

  const handleResetPassword = async (email) => {
    try {
      const res = await api.post(`/api/settings/users/${encodeURIComponent(email)}/reset-password`);
      toast.success(res.data.message || 'Wachtwoord gereset');
      setResetConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset mislukt');
      setResetConfirm(null);
    }
  };

  const handleTogglePermission = async (email, pageKey, currentPerms) => {
    const newPerms = currentPerms.includes(pageKey)
      ? currentPerms.filter(p => p !== pageKey)
      : [...currentPerms, pageKey];
    await handleUpdateUser(email, 'permissions', newPerms);
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (newPw !== confirmPw) {
      setPwError('Wachtwoorden komen niet overeen');
      return;
    }
    if (newPw.length < 6) {
      setPwError('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }
    setPwLoading(true);
    try {
      await api.put('/api/auth/password', {
        huidig_wachtwoord: currentPw,
        nieuw_wachtwoord: newPw
      });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      toast.success('Wachtwoord gewijzigd');
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Wachtwoord wijzigen mislukt');
    } finally {
      setPwLoading(false);
    }
  };

  const loadActivities = useCallback(async () => {
    setActLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (actFilter.gebruiker) params.set('gebruiker', actFilter.gebruiker);
      if (actFilter.actie) params.set('actie', actFilter.actie);
      const res = await api.get(`/api/activity?${params}`);
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error('Activiteiten laden mislukt:', err);
    } finally {
      setActLoading(false);
    }
  }, [actFilter]);

  const loadSessions = async () => {
    setSessLoading(true);
    try {
      const res = await api.get('/api/sessions/active');
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error('Sessies laden mislukt:', err);
    } finally {
      setSessLoading(false);
    }
  };

  const handleSaveScreensaver = async () => {
    try {
      await api.put('/api/settings/app', { screensaver_timeout: screensaverTimeout });
      toast.success('Screensaver instellingen opgeslagen');
    } catch (err) {
      toast.error('Opslaan mislukt');
    }
  };

  const loadEmailStatus = async () => {
    setEmailLoading(true);
    try {
      const [statusRes, statsRes] = await Promise.all([
        api.get('/api/email/status'),
        api.get('/api/email/stats'),
      ]);
      setEmailStatus(statusRes.data);
      setEmailStats(statsRes.data);
    } catch (err) {
      console.error('Email status laden mislukt:', err);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setTestEmailSending(true);
    try {
      const res = await api.post('/api/email/test', {});
      if (res.data.ok) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test e-mail mislukt');
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleTriggerJob = async (type) => {
    setTriggerLoading(type);
    try {
      const res = await api.post(`/api/email/trigger/${type}`, {});
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trigger mislukt');
    } finally {
      setTriggerLoading('');
    }
  };

  const loadDevices = async (email) => {
    setDevicesLoading(true);
    try {
      const res = await api.get(`/api/users/${encodeURIComponent(email)}/devices`);
      setDevices(res.data.devices || []);
      setDeviceUser(email);
    } catch (err) {
      toast.error('Apparaten laden mislukt');
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleClearDevices = async (email) => {
    try {
      await api.delete(`/api/users/${encodeURIComponent(email)}/devices`);
      setDevices([]);
      toast.success('Bekende apparaten gewist');
    } catch (err) {
      toast.error('Wissen mislukt');
    }
  };

  const handleSetup2FA = async () => {
    setTwoFaLoading(true);
    try {
      const res = await api.post('/api/auth/2fa/setup');
      setTwoFaSetup(res.data);
      setTwoFaCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || '2FA setup mislukt');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setTwoFaLoading(true);
    try {
      await api.post('/api/auth/2fa/verify', { code: twoFaCode });
      setTwoFaEnabled(true);
      setTwoFaSetup(null);
      toast.success('2FA succesvol ingeschakeld!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ongeldige code');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePw) { toast.error('Voer je wachtwoord in'); return; }
    setTwoFaLoading(true);
    try {
      await api.post('/api/auth/2fa/disable', { wachtwoord: disablePw });
      setTwoFaEnabled(false);
      setDisablePw('');
      toast.success('2FA uitgeschakeld');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Uitschakelen mislukt');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleAdminReset2FA = async (email) => {
    try {
      await api.post(`/api/auth/2fa/reset/${encodeURIComponent(email)}`);
      toast.success(`2FA gereset voor ${email}`);
    } catch (err) {
      toast.error('Reset mislukt');
    }
  };

  const loadChangelog = async () => {
    try {
      const res = await api.get('/api/changelog');
      setChangelogEntries(res.data.entries || []);
      setChangelogHasNew(res.data.has_new || false);
      // Mark as seen
      await api.post('/api/changelog/seen');
      setChangelogHasNew(false);
    } catch (err) {
      console.error('Changelog laden mislukt:', err);
    }
  };

  const handleAddChangelog = async () => {
    try {
      await api.post('/api/changelog', clForm);
      setShowAddChangelog(false);
      setClForm({ versie: '', titel: '', type: 'nieuw', beschrijving: '' });
      loadChangelog();
      toast.success('Changelog entry toegevoegd');
    } catch (err) {
      toast.error('Toevoegen mislukt');
    }
  };

  const getChangelogIcon = (type) => {
    switch (type) {
      case 'nieuw': return { icon: 'N', color: '#1e8c45', bg: '#1e8c45' };
      case 'fix': return { icon: 'F', color: '#f5a800', bg: '#f5a800' };
      case 'verbetering': return { icon: 'V', color: '#003366', bg: '#003366' };
      default: return { icon: '?', color: '#526071', bg: '#526071' };
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getActieLabel = (actie) => {
    const map = { login: 'Inloggen', paginabezoek: 'Paginabezoek', export: 'Export', 'taak-afvinken': 'Taak afgevinkt' };
    return map[actie] || actie;
  };

  const getActieBadgeColor = (actie) => {
    const map = {
      login: 'bg-blue-100 text-blue-700',
      paginabezoek: 'bg-gray-100 text-gray-600',
      export: 'bg-green-100 text-green-700',
    };
    return map[actie] || 'bg-gray-100 text-gray-600';
  };

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-20 bg-gray-100 rounded-xl" /><div className="h-40 bg-gray-100 rounded-xl" /></div></div>;

  return (
    <div className="tw1-fade-in">
      <HeroBanner
        title="Instellingen"
        subtitle="Beheer gebruikers, rechten, locaties en account"
        variant="default"
        icon={Settings}
      />

      <Tabs defaultValue={admin ? "users" : "account"} className="w-full">
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-xl flex-wrap h-auto gap-1">
          {admin && <TabsTrigger value="users" className="rounded-lg text-sm" data-testid="tab-users">Gebruikers</TabsTrigger>}
          {admin && <TabsTrigger value="permissions" className="rounded-lg text-sm" data-testid="tab-permissions">Rechten</TabsTrigger>}
          {admin && <TabsTrigger value="activity" className="rounded-lg text-sm" data-testid="tab-activity" onClick={loadActivities}>Activiteitenlog</TabsTrigger>}
          {admin && <TabsTrigger value="sessions" className="rounded-lg text-sm" data-testid="tab-sessions" onClick={loadSessions}>Sessies</TabsTrigger>}
          <TabsTrigger value="locations" className="rounded-lg text-sm">Locaties</TabsTrigger>
          <TabsTrigger value="account" className="rounded-lg text-sm">Account</TabsTrigger>
          {admin && <TabsTrigger value="app-settings" className="rounded-lg text-sm" data-testid="tab-app-settings">App</TabsTrigger>}
          {admin && <TabsTrigger value="email" className="rounded-lg text-sm" data-testid="tab-email" onClick={loadEmailStatus}>E-mail</TabsTrigger>}
          <TabsTrigger value="changelog" className="rounded-lg text-sm relative" data-testid="tab-changelog" onClick={loadChangelog}>
            Changelog
            {changelogHasNew && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#f5a800] border-2 border-gray-100" />}
          </TabsTrigger>
          {admin && <TabsTrigger value="grootboek" className="rounded-lg text-sm" data-testid="tab-grootboek">Grootboekrekeningen</TabsTrigger>}
          {admin && <TabsTrigger value="phone-duty" className="rounded-lg text-sm" data-testid="tab-phone-duty">Telefoondienst</TabsTrigger>}
        </TabsList>

        {/* ─── Users Tab ─── */}
        {admin && (
          <TabsContent value="users">
            <div className="tw1-card overflow-hidden" data-testid="settings-users-table">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-[#0b1220]">Gebruikersbeheer</h3>
                <Button
                  data-testid="settings-add-user-button"
                  onClick={() => setShowAddUser(true)}
                  className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-sm"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1" /> Toevoegen
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full tw1-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left">Naam</th>
                      <th className="px-4 py-3 text-left">E-mail</th>
                      <th className="px-4 py-3 text-left">Rol</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-right">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#003366]/10 flex items-center justify-center">
                              <span className="text-xs font-bold text-[#003366]">{u.naam?.[0]}</span>
                            </div>
                            <span className="text-sm font-medium">{u.naam}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-[#526071]">{u.email}</td>
                        <td className="px-4 py-3">
                          <Select
                            value={u.rol}
                            onValueChange={(val) => handleUpdateUser(u.email, 'rol', val)}
                          >
                            <SelectTrigger className="w-[130px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">
                                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Admin</span>
                              </SelectItem>
                              <SelectItem value="gebruiker">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" /> Gebruiker</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge
                            variant={u.actief !== false ? 'default' : 'destructive'}
                            className={`text-xs ${u.actief !== false ? 'bg-[#1e8c45]/10 text-[#1e8c45] hover:bg-[#1e8c45]/10' : ''}`}
                          >
                            {u.actief !== false ? 'Actief' : 'Inactief'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() => handleUpdateUser(u.email, 'actief', u.actief === false)}
                            >
                              {u.actief !== false ? 'Deactiveren' : 'Activeren'}
                            </Button>
                            {admin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#003366]"
                                onClick={() => setResetConfirm(u)}
                                data-testid={`reset-pw-${u.email}`}
                              >
                                <KeyRound className="w-3 h-3 mr-1" /> Reset ww
                              </Button>
                            )}
                            {admin && u.totp_enabled && (
                              <Button variant="ghost" size="sm" className="text-xs text-[#c0392b]" onClick={() => handleAdminReset2FA(u.email)}>
                                2FA reset
                              </Button>
                            )}
                            {admin && u.email !== 'johannes@truckwash1group.nl' && u.email !== currentUser?.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#c0392b] hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteConfirm(u)}
                                data-testid={`delete-user-${u.email}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add User Dialog */}
            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Gebruiker Toevoegen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Naam</Label>
                    <Input value={newUser.naam} onChange={(e) => setNewUser({ ...newUser, naam: e.target.value })} placeholder="Volledige naam" className="mt-1" />
                  </div>
                  <div>
                    <Label>E-mailadres</Label>
                    <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="naam@truckwash1group.nl" className="mt-1" />
                  </div>
                  <div>
                    <Label>Rol</Label>
                    <Select value={newUser.rol} onValueChange={(val) => setNewUser({ ...newUser, rol: val })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="gebruiker">Gebruiker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Wachtwoord</Label>
                    <Input value={newUser.wachtwoord} onChange={(e) => setNewUser({ ...newUser, wachtwoord: e.target.value })} className="mt-1" />
                  </div>
                  {addError && <p className="text-sm text-[#c0392b]">{addError}</p>}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddUser(false)}>Annuleren</Button>
                  <Button onClick={handleAddUser} className="bg-[#003366] hover:bg-[#00244d] text-white">Toevoegen</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete User Confirmation Dialog */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-[#c0392b]">
                    <AlertTriangle className="w-5 h-5" /> Gebruiker verwijderen
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[#0b1220]">
                  Weet je zeker dat je <strong>{deleteConfirm?.naam}</strong> ({deleteConfirm?.email}) wilt verwijderen? Dit kan niet ongedaan worden.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuleren</Button>
                  <Button
                    onClick={() => handleDeleteUser(deleteConfirm?.email)}
                    className="bg-[#c0392b] hover:bg-red-700 text-white"
                    data-testid="confirm-delete-user"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Reset Password Confirmation Dialog */}
            <Dialog open={!!resetConfirm} onOpenChange={() => setResetConfirm(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-[#003366]">
                    <KeyRound className="w-5 h-5" /> Wachtwoord resetten
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[#0b1220]">
                  Het wachtwoord van <strong>{resetConfirm?.naam}</strong> wordt gereset naar het standaard wachtwoord. De gebruiker ontvangt een e-mailnotificatie.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setResetConfirm(null)}>Annuleren</Button>
                  <Button
                    onClick={() => handleResetPassword(resetConfirm?.email)}
                    className="bg-[#003366] hover:bg-[#00244d] text-white"
                    data-testid="confirm-reset-pw"
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> Reset wachtwoord
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* ─── Permissions Tab ─── */}
        {admin && (
          <TabsContent value="permissions">
            <div className="tw1-card overflow-hidden" data-testid="permissions-matrix">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-sm text-[#0b1220] flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Paginatoegang per gebruiker
                </h3>
                <p className="text-xs text-[#526071] mt-1">Admin-gebruikers hebben altijd volledige toegang</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide text-[#526071] sticky left-0 bg-gray-50 z-10 min-w-[140px]">Gebruiker</th>
                      {ALL_PAGES.map(p => (
                        <th key={p.key} className="px-2 py-3 text-center font-medium text-[10px] uppercase tracking-wide text-[#526071] min-w-[80px]">
                          {p.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => {
                      const isAdminUser = u.rol === 'admin';
                      const perms = u.permissions || [];
                      return (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 sticky left-0 bg-white z-10">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-[#003366]/10 flex items-center justify-center">
                                <span className="text-[10px] font-bold text-[#003366]">{u.naam?.[0]}</span>
                              </div>
                              <div>
                                <span className="text-sm font-medium block">{u.naam}</span>
                                <span className="text-[10px] text-[#526071]">{u.rol}</span>
                              </div>
                            </div>
                          </td>
                          {ALL_PAGES.map(p => (
                            <td key={p.key} className="px-2 py-2.5 text-center">
                              <button
                                data-testid={`perm-${u.email}-${p.key}`}
                                disabled={isAdminUser}
                                onClick={() => !isAdminUser && handleTogglePermission(u.email, p.key, perms)}
                                className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                                  isAdminUser
                                    ? 'text-[#1e8c45]/40 cursor-not-allowed'
                                    : perms.includes(p.key)
                                      ? 'text-[#1e8c45] hover:bg-[#1e8c45]/10'
                                      : 'text-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {(isAdminUser || perms.includes(p.key))
                                  ? <CheckSquare className="w-5 h-5" />
                                  : <Square className="w-5 h-5" />
                                }
                              </button>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        )}

        {/* ─── Activity Log Tab ─── */}
        {admin && (
          <TabsContent value="activity">
            <div className="tw1-card overflow-hidden" data-testid="activity-log">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-sm text-[#0b1220] flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Activiteitenlog
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <Select value={actFilter.gebruiker || 'all'} onValueChange={(v) => setActFilter(f => ({ ...f, gebruiker: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue placeholder="Alle gebruikers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle gebruikers</SelectItem>
                      {users.map(u => <SelectItem key={u.email} value={u.email}>{u.naam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={actFilter.actie || 'all'} onValueChange={(v) => setActFilter(f => ({ ...f, actie: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-[160px] h-8 text-xs">
                      <SelectValue placeholder="Alle acties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle acties</SelectItem>
                      <SelectItem value="login">Inloggen</SelectItem>
                      <SelectItem value="paginabezoek">Paginabezoek</SelectItem>
                      <SelectItem value="export">Export</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={loadActivities} className="h-8 text-xs bg-[#003366] hover:bg-[#00244d] text-white">
                    <Filter className="w-3 h-3 mr-1" /> Filteren
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                {actLoading ? (
                  <div className="p-8 text-center text-sm text-[#526071]">Laden...</div>
                ) : activities.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#526071]">Geen activiteiten gevonden</div>
                ) : (
                  <table className="w-full tw1-table">
                    <thead className="sticky top-0 bg-white">
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left">Datum/Tijd</th>
                        <th className="px-4 py-2 text-left">Gebruiker</th>
                        <th className="px-4 py-2 text-left">Actie</th>
                        <th className="px-4 py-2 text-left">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((a, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2 text-xs text-[#526071] whitespace-nowrap">{formatDate(a.datum)}</td>
                          <td className="px-4 py-2 text-sm font-medium">{a.gebruiker}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${getActieBadgeColor(a.actie)}`}>
                              {getActieLabel(a.actie)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-[#526071] max-w-[300px] truncate">{a.details || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* ─── Sessions Tab ─── */}
        {admin && (
          <TabsContent value="sessions">
            <div className="tw1-card overflow-hidden" data-testid="sessions-panel">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-sm text-[#0b1220] flex items-center gap-2">
                    <Monitor className="w-4 h-4" /> Actieve Sessies
                  </h3>
                  <p className="text-xs text-[#526071] mt-1">Gebruikers actief in de laatste 30 minuten</p>
                </div>
                <Button size="sm" onClick={loadSessions} className="h-8 text-xs bg-[#003366] hover:bg-[#00244d] text-white">
                  Vernieuwen
                </Button>
              </div>
              {sessLoading ? (
                <div className="p-8 text-center text-sm text-[#526071]">Laden...</div>
              ) : sessions.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#526071]">Geen actieve sessies</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {sessions.map((s, i) => {
                    const lastActive = s.last_active ? new Date(s.last_active) : null;
                    const minutesAgo = lastActive ? Math.round((Date.now() - lastActive.getTime()) / 60000) : null;
                    return (
                      <div key={i} className="flex items-center gap-4 px-4 py-3">
                        <div className="relative">
                          <div className="w-9 h-9 rounded-full bg-[#003366]/10 flex items-center justify-center">
                            <span className="text-xs font-bold text-[#003366]">{s.naam?.[0]}</span>
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#1e8c45] border-2 border-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0b1220]">{s.naam}</p>
                          <p className="text-xs text-[#526071]">{s.email}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-[#003366]/10 text-[#003366] hover:bg-[#003366]/10 text-[10px]">{s.rol}</Badge>
                          <p className="text-[10px] text-[#526071] mt-1 flex items-center gap-1 justify-end">
                            <Clock className="w-3 h-3" />
                            {minutesAgo === 0 ? 'Nu actief' : `${minutesAgo} min geleden`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* ─── Locations Tab ─── */}
        <TabsContent value="locations">
          <div className="tw1-card overflow-hidden" data-testid="settings-locations-list">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-sm text-[#0b1220]">Waslocaties ({locationDetails.length})</h3>
              <p className="text-xs text-[#526071] mt-1">Klik op een locatie voor relatiecodes en Exact configuratie</p>
            </div>
            <div className="divide-y divide-gray-50">
              {locationDetails.map((loc, i) => (
                <div key={i}>
                  <button
                    onClick={() => setExpandedLoc(expandedLoc === i ? null : i)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#003366]/10 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-[#003366]" />
                      </div>
                      <div className="text-left">
                        <span className="text-sm font-medium text-[#0b1220]">{loc.naam}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-mono text-[#526071]">GB: {loc.gb_holding}</span>
                          <span className="text-[10px] text-[#526071]">Fee: {(loc.fee_pct * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                    {expandedLoc === i ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </button>
                  {expandedLoc === i && (
                    <div className="px-4 pb-4 pt-1 bg-gray-50/50">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-[10px] uppercase tracking-wide text-[#526071] font-medium">RC Group-Locatie</p>
                          <p className="text-sm font-mono font-bold text-[#003366] mt-1">{loc.rc_group_loc}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-[10px] uppercase tracking-wide text-[#526071] font-medium">RC Locatie-Group</p>
                          <p className="text-sm font-mono font-bold text-[#003366] mt-1">{loc.rc_loc_group}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-[10px] uppercase tracking-wide text-[#526071] font-medium">GB Holding</p>
                          <p className="text-sm font-mono font-bold text-[#f5a800] mt-1">{loc.gb_holding}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-[10px] uppercase tracking-wide text-[#526071] font-medium">Fee Percentage</p>
                          <p className="text-sm font-bold mt-1" style={{ color: loc.fee_pct === 0.05 ? '#ff6200' : '#1e8c45' }}>{(loc.fee_pct * 100).toFixed(0)}%</p>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-[#526071]">
                        Dagboek 75 (Verkoop) &bull; Dagboek 65 (Group Inkoop) &bull; Dagboek 60 (Holding Inkoop)
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ─── Account Tab ─── */}
        <TabsContent value="account">
          <div className="max-w-md space-y-6">
            <div className="tw1-card p-5">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-4 flex items-center gap-2">
                <User className="w-4 h-4" /> Profiel
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-[#526071]">Naam</Label>
                  <p className="text-sm font-medium mt-0.5">{currentUser?.naam}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#526071]">E-mail</Label>
                  <p className="text-sm font-mono mt-0.5">{currentUser?.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#526071]">Rol</Label>
                  <Badge className="mt-0.5 bg-[#003366]/10 text-[#003366] hover:bg-[#003366]/10">{currentUser?.rol}</Badge>
                </div>
                <div>
                  <Button
                    data-testid="restart-tour-btn"
                    variant="outline"
                    size="sm"
                    className="text-xs mt-2"
                    onClick={async () => {
                      await api.post('/api/auth/tour-reset');
                      toast.success('Tour gereset — herlaad de pagina om de tour opnieuw te starten');
                    }}
                  >
                    Rondleiding opnieuw starten
                  </Button>
                </div>
              </div>
            </div>

            <div className="tw1-card p-5">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-4 flex items-center gap-2">
                <Lock className="w-4 h-4" /> Wachtwoord Wijzigen
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Huidig wachtwoord</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      className="h-9 text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Nieuw wachtwoord</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className="h-9 text-sm pr-10"
                      placeholder="Minimaal 6 tekens"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Bevestig nieuw wachtwoord</Label>
                  <Input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    className="mt-1 h-9 text-sm"
                  />
                </div>
                {pwError && <p className="text-sm text-[#c0392b]">{pwError}</p>}
                <Button
                  onClick={handleChangePassword}
                  disabled={!currentPw || !newPw || !confirmPw || pwLoading}
                  className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-sm"
                >
                  {pwLoading ? 'Bezig...' : 'Wachtwoord Wijzigen'}
                </Button>
              </div>
            </div>

            {/* 2FA Section */}
            <div className="tw1-card p-5" data-testid="2fa-settings">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Twee-factor authenticatie (2FA)
              </h3>
              <p className="text-xs text-[#526071] mb-4">
                Voeg een extra beveiligingslaag toe aan je account met een authenticator app.
              </p>
              {twoFaEnabled ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#1e8c45]" />
                    <span className="font-medium text-[#1e8c45]">2FA is ingeschakeld</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="password" placeholder="Huidig wachtwoord" value={disablePw} onChange={e => setDisablePw(e.target.value)} className="h-9 text-sm max-w-[200px]" />
                    <Button size="sm" variant="outline" onClick={handleDisable2FA} disabled={twoFaLoading} className="text-xs text-[#c0392b] border-[#c0392b]/30 hover:bg-[#c0392b]/5">
                      2FA uitschakelen
                    </Button>
                  </div>
                </div>
              ) : twoFaSetup ? (
                <div className="space-y-4">
                  <p className="text-xs text-[#526071]">Scan de QR-code met Google Authenticator, Authy of een andere TOTP app:</p>
                  <div className="flex justify-center p-4 bg-white rounded-xl border border-gray-100">
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(twoFaSetup.uri)}`} alt="2FA QR Code" className="w-44 h-44" />
                  </div>
                  <div className="text-xs">
                    <p className="text-[#526071] mb-1">Of voer deze sleutel handmatig in:</p>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-3 py-1.5 rounded-lg font-mono text-xs select-all">{twoFaSetup.secret}</code>
                      <button onClick={() => { navigator.clipboard.writeText(twoFaSetup.secret); toast.success('Gekopieerd!'); }} className="text-[#003366] hover:text-[#00244d]">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Verificatiecode</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        data-testid="2fa-verify-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        value={twoFaCode}
                        onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                        className="h-9 w-32 text-center font-mono tracking-widest text-sm"
                      />
                      <Button size="sm" onClick={handleVerify2FA} disabled={twoFaCode.length < 6 || twoFaLoading} className="bg-[#003366] hover:bg-[#00244d] text-white text-xs">
                        Verifiëren
                      </Button>
                    </div>
                  </div>
                  <div className="bg-[#f5a800]/5 border border-[#f5a800]/20 rounded-xl p-3">
                    <p className="text-xs font-medium text-[#0b1220] mb-2">Backup codes (bewaar deze veilig!):</p>
                    <div className="grid grid-cols-2 gap-1">
                      {twoFaSetup.backup_codes?.map((c, i) => (
                        <code key={i} className="text-[11px] font-mono bg-white px-2 py-0.5 rounded border border-gray-100">{c}</code>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Button size="sm" onClick={handleSetup2FA} disabled={twoFaLoading} className="bg-[#003366] hover:bg-[#00244d] text-white text-xs">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> 2FA inschakelen
                </Button>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ─── App Settings Tab ─── */}
        {admin && (
          <TabsContent value="app-settings">
            <div className="max-w-md space-y-4">
              <div className="tw1-card p-5" data-testid="screensaver-settings">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-4 flex items-center gap-2">
                  <Timer className="w-4 h-4" /> Screensaver
                </h3>
                <p className="text-xs text-[#526071] mb-4">
                  Na de ingestelde tijd zonder activiteit wordt een screensaver getoond met het TW1 logo.
                </p>
                <div className="flex items-center gap-3">
                  <Label className="text-sm whitespace-nowrap">Timeout (minuten)</Label>
                  <Input
                    data-testid="screensaver-timeout-input"
                    type="number"
                    min={1}
                    max={60}
                    value={screensaverTimeout}
                    onChange={(e) => setScreensaverTimeout(parseInt(e.target.value) || 5)}
                    className="w-24 h-9 text-sm"
                  />
                  <Button
                    onClick={handleSaveScreensaver}
                    className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-sm"
                    size="sm"
                  >
                    Opslaan
                  </Button>
                </div>
              </div>
              <div className="tw1-card p-5" data-testid="seasonal-settings">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-2 flex items-center gap-2">
                  Seizoensthema's
                </h3>
                <p className="text-xs text-[#526071] mb-4">
                  Subtiele visuele effecten in de sidebar op basis van het seizoen (sneeuw in december, blaadjes in de herfst, etc.)
                </p>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="seasonal-toggle"
                    onClick={async () => {
                      const next = !seasonalThemes;
                      setSeasonalThemes(next);
                      try {
                        await api.put('/api/settings/app', { seasonal_themes: next });
                        toast.success(next ? 'Seizoensthema\'s ingeschakeld' : 'Seizoensthema\'s uitgeschakeld');
                      } catch { toast.error('Opslaan mislukt'); }
                    }}
                    className={`relative w-11 h-6 rounded-full transition-colors ${seasonalThemes ? 'bg-[#1e8c45]' : 'bg-gray-300'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${seasonalThemes ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                  <Label className="text-sm">{seasonalThemes ? 'Actief' : 'Uit'}</Label>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* ─── Email Tab ─── */}
        {admin && (
          <TabsContent value="email">
            <div className="space-y-6 max-w-2xl">
              {/* Resend Status */}
              <div className="tw1-card p-5" data-testid="email-status-card">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-4 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> E-mail Configuratie (Resend)
                </h3>
                {emailLoading ? (
                  <div className="text-sm text-[#526071]">Laden...</div>
                ) : emailStatus ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {emailStatus.resend_configured ? (
                        <CheckCircle className="w-4 h-4 text-[#1e8c45]" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-[#f59e0b]" />
                      )}
                      <span className="text-sm font-medium">
                        Resend: {emailStatus.resend_configured ? 'Geconfigureerd' : 'Niet geconfigureerd'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-[#526071] text-[10px] uppercase tracking-wide font-medium">Afzender</p>
                        <p className="font-mono mt-1">{emailStatus.resend_from || 'niet ingesteld'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-[#526071] text-[10px] uppercase tracking-wide font-medium">Domein</p>
                        <p className="font-mono mt-1">{emailStatus.resend_domain || 'niet ingesteld'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-[#526071] text-[10px] uppercase tracking-wide font-medium">Beveiligings e-mail</p>
                        <p className="font-mono mt-1">{emailStatus.security_email}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-[#526071] text-[10px] uppercase tracking-wide font-medium">Scheduler</p>
                        <div className="mt-1">{emailStatus.scheduler_running ? <Badge className="bg-[#1e8c45]/10 text-[#1e8c45] hover:bg-[#1e8c45]/10 text-[10px]">Actief</Badge> : <Badge variant="destructive" className="text-[10px]">Inactief</Badge>}</div>
                      </div>
                    </div>
                    {emailStatus.jobs?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-[10px] uppercase tracking-wide text-[#526071] font-medium mb-2">Geplande jobs ({emailStatus.jobs.length})</p>
                        <div className="space-y-1.5">
                          {emailStatus.jobs.map((job, i) => {
                            const labels = { daily_tasks: 'Dagelijks overzicht (07:00)', forgotten_tasks: 'Vergeten taken (18:00)', task_reminders: 'Taak-herinneringen (07:00)', daily_summary: 'Dagelijkse samenvatting (07:15)', weekly_report: 'Weekrapport (ma 07:30)' };
                            return (
                              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-xs">
                                <span className="font-medium">{labels[job.id] || job.id}</span>
                                <span className="text-[#526071] font-mono text-[10px]">{job.next_run ? new Date(job.next_run).toLocaleString('nl-NL') : '-'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                      <Button
                        data-testid="send-test-email-btn"
                        size="sm"
                        onClick={handleSendTestEmail}
                        disabled={testEmailSending}
                        className="h-8 text-xs bg-[#003366] hover:bg-[#00244d] text-white"
                      >
                        <Send className="w-3 h-3 mr-1" /> {testEmailSending ? 'Verzenden...' : 'Test E-mail naar mij'}
                      </Button>
                      <Button
                        data-testid="trigger-daily-btn"
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerJob('daily')}
                        disabled={!!triggerLoading}
                        className="h-8 text-xs"
                      >
                        <Play className="w-3 h-3 mr-1" /> {triggerLoading === 'daily' ? 'Bezig...' : 'Dagelijks overzicht'}
                      </Button>
                      <Button
                        data-testid="trigger-task-reminders-btn"
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerJob('task-reminders')}
                        disabled={!!triggerLoading}
                        className="h-8 text-xs"
                      >
                        <Play className="w-3 h-3 mr-1" /> {triggerLoading === 'task-reminders' ? 'Bezig...' : 'Taak-herinneringen'}
                      </Button>
                      <Button
                        data-testid="trigger-daily-summary-btn"
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerJob('daily-summary')}
                        disabled={!!triggerLoading}
                        className="h-8 text-xs"
                      >
                        <Play className="w-3 h-3 mr-1" /> {triggerLoading === 'daily-summary' ? 'Bezig...' : 'Dagelijkse samenvatting'}
                      </Button>
                      <Button
                        data-testid="trigger-weekly-report-btn"
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerJob('weekly-report')}
                        disabled={!!triggerLoading}
                        className="h-8 text-xs"
                      >
                        <Play className="w-3 h-3 mr-1" /> {triggerLoading === 'weekly-report' ? 'Bezig...' : 'Weekrapport'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#526071]">Klik op het E-mail tabblad om de status te laden</p>
                )}
              </div>

              {/* Email Stats */}
              {emailStats && (
                <div className="tw1-card p-5" data-testid="email-stats-card">
                  <h3 className="font-semibold text-sm text-[#0b1220] mb-1">E-mail Statistieken</h3>
                  <p className="text-xs text-[#526071] mb-3">Deze maand ({emailStats.month}): <strong>{emailStats.total}</strong> e-mails verstuurd ({emailStats.total_success} succesvol)</p>
                  {emailStats.breakdown?.length > 0 && (
                    <div className="space-y-1.5">
                      {emailStats.breakdown.map((b, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-xs">
                          <span className="font-medium text-[#0b1220]">{b.label}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#526071] font-mono">{b.count}x</span>
                            {b.count !== b.success && <span className="text-[10px] text-[#c0392b]">({b.count - b.success} mislukt)</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Email Preferences */}
              <EmailPreferencesSection />

              {/* Known Devices */}
              <div className="tw1-card p-5" data-testid="known-devices-card">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-4 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" /> Bekende Apparaten
                </h3>
                <p className="text-xs text-[#526071] mb-4">
                  Bij elke login wordt het apparaat opgeslagen. Bij een onbekend apparaat wordt een beveiligingsmelding verstuurd.
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <Select value={deviceUser || 'none'} onValueChange={(v) => v !== 'none' && loadDevices(v)}>
                    <SelectTrigger className="w-[220px] h-8 text-xs" data-testid="device-user-select">
                      <SelectValue placeholder="Selecteer gebruiker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Selecteer gebruiker</SelectItem>
                      {users.map(u => <SelectItem key={u.email} value={u.email}>{u.naam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {deviceUser && devices.length > 0 && (
                    <Button
                      data-testid="clear-devices-btn"
                      size="sm"
                      variant="outline"
                      onClick={() => handleClearDevices(deviceUser)}
                      className="h-8 text-xs text-[#c0392b] border-[#c0392b]/30 hover:bg-[#c0392b]/5"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Apparaten wissen
                    </Button>
                  )}
                </div>
                {devicesLoading ? (
                  <div className="text-sm text-[#526071]">Laden...</div>
                ) : deviceUser && devices.length === 0 ? (
                  <div className="text-sm text-[#526071] bg-gray-50 rounded-lg p-4 text-center">Geen bekende apparaten</div>
                ) : devices.length > 0 ? (
                  <div className="space-y-2">
                    {devices.map((d, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3 text-xs">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#0b1220] truncate">{d.user_agent}</p>
                            <div className="flex items-center gap-3 mt-1 text-[#526071]">
                              <span>IP: <span className="font-mono">{d.ip}</span></span>
                              <span>Eerste keer: {formatDate(d.first_seen)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </TabsContent>
        )}

        {/* ─── Changelog Tab ─── */}
        <TabsContent value="changelog">
          <div className="max-w-2xl space-y-4">
            {admin && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowAddChangelog(!showAddChangelog)} className="bg-[#003366] hover:bg-[#00244d] text-white text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nieuwe entry
                </Button>
              </div>
            )}
            {showAddChangelog && admin && (
              <div className="tw1-card p-5 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Versie</Label>
                    <Input value={clForm.versie} onChange={e => setClForm(f => ({ ...f, versie: e.target.value }))} placeholder="v2.5" className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Titel</Label>
                    <Input value={clForm.titel} onChange={e => setClForm(f => ({ ...f, titel: e.target.value }))} placeholder="Nieuwe feature..." className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={clForm.type} onValueChange={v => setClForm(f => ({ ...f, type: v }))}>
                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nieuw">Nieuw</SelectItem>
                        <SelectItem value="fix">Fix</SelectItem>
                        <SelectItem value="verbetering">Verbetering</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Beschrijving</Label>
                  <textarea
                    value={clForm.beschrijving}
                    onChange={e => setClForm(f => ({ ...f, beschrijving: e.target.value }))}
                    placeholder="Wat is er gewijzigd..."
                    className="mt-1 w-full h-20 rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366]/40"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddChangelog} className="bg-[#003366] hover:bg-[#00244d] text-white text-xs">Opslaan</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddChangelog(false)} className="text-xs">Annuleren</Button>
                </div>
              </div>
            )}
            {changelogEntries.length === 0 ? (
              <div className="tw1-card p-10 text-center text-sm text-[#526071]">
                Nog geen changelog entries
              </div>
            ) : (
              <div className="space-y-3">
                {changelogEntries.map((entry, i) => {
                  const cfg = getChangelogIcon(entry.type);
                  return (
                    <div key={i} className="tw1-card p-4 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold" style={{ background: cfg.bg }}>
                          {cfg.icon}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {entry.versie && <Badge className="bg-[#003366]/10 text-[#003366] hover:bg-[#003366]/10 text-[10px]">{entry.versie}</Badge>}
                          <Badge className="text-[10px]" style={{ background: `${cfg.bg}15`, color: cfg.color }}>
                            {entry.type === 'nieuw' ? 'Nieuw' : entry.type === 'fix' ? 'Fix' : 'Verbetering'}
                          </Badge>
                          <span className="text-[10px] text-[#526071] ml-auto">
                            {entry.datum ? new Date(entry.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-[#0b1220]">{entry.titel}</p>
                        {entry.beschrijving && <p className="text-xs text-[#526071] mt-1 whitespace-pre-line">{entry.beschrijving}</p>}
                        {entry.auteur && <p className="text-[10px] text-[#8899b4] mt-1">Door {entry.auteur}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ─── Grootboekrekeningen Tab ─── */}
        {admin && (
          <TabsContent value="grootboek">
            <GrootboekTab />
          </TabsContent>
        )}

        {/* ─── Telefoondienst Tab ─── */}
        {admin && (
          <TabsContent value="phone-duty">
            <PhoneDutyTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
